import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Link, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store';
import TagManager from 'react-gtm-module';
import Home from './pages/home/Home';
import AboutUs from './pages/AboutUs';
import GetFranchise from './pages/franchise/GetFranchise';
import ThankYou from './pages/franchise/ThankYou';
import FranchiseLogin from './pages/franchise/FranchiseLogin';
import FranchiseDashboard from './pages/franchise/FranchiseDashboard';
import StoreSelection from './pages/order/StoreSelection';
import OrderPlacement from './pages/order/OrderPlacement';
import OrderThankYou from './pages/order/OrderThankYou';
import OurStores from './pages/OurStores';
import OurServices from './pages/services/OurServices';
import ContactUs from './pages/ContactUs';
import Order from './pages/Order';
import InfoLogin from './pages/info/InfoLogin';
import TermsAndConditions from './pages/TermsAndConditions';
import WhatsAppButton from './pages/WhatsAppButton';
import FinancePortalButton from './finance/FinancePortalButton';
import './App.css';
import logo from './assets/logo_v3.png'

import RequestInfo from './pages/info/RequestInfo';
import ScrollToTop from './pages/ScrollToTop';
import PrivacyPolicy from './pages/PrivacyPolicy';
import StatsInitializer from './components/StatsInitializer';
import FinanceApp from './finance/FinanceApp';

// GTM Configuration
const gtmId = 'GTM-5MLBDXBZ';
const tagManagerArgs = {
  gtmId: gtmId,
  dataLayer: {
    page: window.location.pathname + window.location.search
  }
};

// Component to track route changes
function RouteTracker() {
  const location = useLocation();

  useEffect(() => {
    // Track page views on route change
    TagManager.dataLayer({
      dataLayer: {
        event: 'pageview',
        page: location.pathname + location.search,
        title: document.title
      }
    });
  }, [location]);

  return null;
}

function AppShell() {
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef(null);
  const location = useLocation();
  const isFinanceRoute = location.pathname.startsWith('/finance');

  useEffect(() => {
    if (isFinanceRoute) {
      document.body.classList.add('finance-route');
    } else {
      document.body.classList.remove('finance-route');
    }
    return () => document.body.classList.remove('finance-route');
  }, [isFinanceRoute]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      if (navRef.current) {
        if (window.scrollY > 10) {
          navRef.current.classList.add("scrolled");
        } else {
          navRef.current.classList.remove("scrolled");
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <RouteTracker />
      <ScrollToTop />
      {!isFinanceRoute && <WhatsAppButton />}
      {!isFinanceRoute && <FinancePortalButton />}
      {!isFinanceRoute && (
        <div className="info-bar" id="infoBar">
          <div className="info-bar-content">
            <div className="info-left">
              <span><i className="fas fa-map-marker-alt"></i> B-10, Ground Floor G-02, Sector-2, Noida, UP, 201301</span>
              <span><i className="fas fa-phone"></i> +91 8448717900</span>
              <span><i className="fas fa-envelope"></i> support@neatnclean.in</span>
            </div>
          </div>
        </div>
      )}
      {!isFinanceRoute && (
        <header className="navbar" ref={navRef}>
          <Link to="/" className="logo" onClick={() => setMenuOpen(false)}>
            <img src={logo} alt="Cleanz24 Logo" style={{ height: '50px', width: 'auto'}} />
          </Link>
          <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>
            ☰
          </button>
          <nav className={menuOpen ? "nav-links open" : "nav-links"}>
            <NavLink to="/" exact="true" onClick={() => setMenuOpen(false)}>Home</NavLink>
            <NavLink to="/about" onClick={() => setMenuOpen(false)}>About Us</NavLink>
            <NavLink to="/order" onClick={() => setMenuOpen(false)}>Order</NavLink>
            <NavLink to="/franchise" onClick={() => setMenuOpen(false)}>Get Franchise</NavLink>
            <NavLink to="/stores" onClick={() => setMenuOpen(false)}>Our Stores</NavLink>
            <NavLink to="/services" onClick={() => setMenuOpen(false)}>Our Services</NavLink>
            <NavLink to="/contact" onClick={() => setMenuOpen(false)}>Contact Us</NavLink>
          </nav>
        </header>
      )}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/order" element={<Order />} />
        <Route path="/franchise" element={<GetFranchise />} />
        <Route path="/franchise/thank-you" element={<ThankYou />} />
        <Route path="/franchise/login" element={<FranchiseLogin />} />
        <Route path="/franchise/dashboard" element={<FranchiseDashboard />} />
        <Route path="/order/stores" element={<StoreSelection />} />
        <Route path="/order/place" element={<OrderPlacement />} />
        <Route path="/order/thank-you" element={<OrderThankYou />} />
        <Route path="/stores" element={<OurStores />} />
        <Route path="/services" element={<OurServices />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/requests/infoLogin" element={<InfoLogin />} />
        <Route path="/requests/requestInfo" element={<RequestInfo />} />
        <Route path="/finance/*" element={<FinanceApp />} />
      </Routes>
    </>
  );
}

function App() {
  useEffect(() => {
    TagManager.initialize(tagManagerArgs);
  }, []);

  return (
    <Provider store={store}>
      <StatsInitializer />
      <Router>
        <AppShell />
      </Router>
    </Provider>
  );
}

export default App;
